import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getResources, deleteResource } from '../../../api';
import {
    getYouTubeEmbedUrl,
    getYouTubeThumbnail,
    getSourceUrl,
    getSourceTitle,
    getSourceDescription,
    getSourceAuthorOrTopic,
    getSourceType
} from './sourceUtils';

const SouceList = () => {
    const navigate = useNavigate();
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedType, setSelectedType] = useState('all'); // 'all' | 'image' | 'youtube_video'
    const [searchQuery, setSearchQuery] = useState('');

    // Modal state for viewing/previewing source
    const [activePreview, setActivePreview] = useState(null); // HookResource object
    // Modal state for deleting
    const [deletingSource, setDeletingSource] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    
    // Toast notification state
    const [toastMessage, setToastMessage] = useState(null);

    const showToast = (msg, type = 'success') => {
        setToastMessage({ text: msg, type });
        setTimeout(() => setToastMessage(null), 3500);
    };

    const fetchSources = useCallback(async (typeFilter = selectedType) => {
        try {
            setLoading(true);
            setError('');
            const data = await getResources(typeFilter === 'all' ? '' : typeFilter);
            setSources(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching sources:', err);
            setError(err.response?.data?.message || 'Failed to load resources from server.');
        } finally {
            setLoading(false);
        }
    }, [selectedType]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }
        fetchSources();
    }, [navigate, fetchSources]);

    const handleTypeFilterChange = (type) => {
        setSelectedType(type);
        fetchSources(type);
    };

    const handleDeleteConfirm = async () => {
        if (!deletingSource) return;
        try {
            setIsDeleting(true);
            await deleteResource(deletingSource.id || deletingSource._id);
            setSources(prev => prev.filter(s => (s.id || s._id) !== (deletingSource.id || deletingSource._id)));
            showToast('Source deleted successfully!');
            setDeletingSource(null);
        } catch (err) {
            console.error('Error deleting resource:', err);
            showToast(err.response?.data?.message || 'Failed to delete resource.', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const copyToClipboard = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        showToast('Link copied to clipboard!');
    };

    // Filter sources by search query & selected type
    const filteredSources = useMemo(() => {
        return sources.filter(source => {
            const title = getSourceTitle(source).toLowerCase();
            const url = getSourceUrl(source).toLowerCase();
            const desc = getSourceDescription(source).toLowerCase();
            const authorOrTopic = getSourceAuthorOrTopic(source).toLowerCase();
            const type = getSourceType(source);
            
            const q = searchQuery.toLowerCase();
            const matchesSearch = title.includes(q) ||
                url.includes(q) ||
                desc.includes(q) ||
                authorOrTopic.includes(q);
            
            const matchesType = selectedType === 'all' || type === selectedType;

            return matchesSearch && matchesType;
        });
    }, [sources, searchQuery, selectedType]);

    const token = localStorage.getItem('token');
    if (!token) return null;

    return (
        <div style={{
            minHeight: '100vh',
            background: '#ccdff5',
            padding: '40px 20px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
            {/* Toast Notification */}
            {toastMessage && (
                <div style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    zIndex: 9999,
                    background: toastMessage.type === 'error' ? '#ef4444' : '#10b981',
                    color: '#ffffff',
                    padding: '12px 20px',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                    fontWeight: '600',
                    fontSize: '0.95rem'
                }}>
                    {toastMessage.text}
                </div>
            )}

            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                background: '#F6F6F6',
                borderRadius: '20px',
                padding: '40px',
                boxShadow: '0 4px 24px rgba(44, 62, 80, 0.08)'
            }}>
                {/* Header & Navigation */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '30px',
                    borderBottom: '2px solid #e2e8f0',
                    paddingBottom: '20px'
                }}>
                    <div>
                        <h1 style={{
                            color: '#3c5a5d',
                            margin: '0 0 6px 0',
                            fontSize: '2.5rem',
                            fontWeight: '800'
                        }}>
                            Sources Library
                        </h1>
                        <p style={{ margin: 0, color: '#707F81', fontSize: '1rem', fontWeight: '500' }}>
                            Visualize, manage, and inspect image & video resources
                        </p>
                    </div>

                    <button
                        onClick={() => navigate('/create-source')}
                        style={{
                            background: '#3c5a5d',
                            color: '#ffffff',
                            border: 'none',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            padding: '12px 20px',
                            borderRadius: '10px',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#2c4345'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#3c5a5d'; }}
                    >
                        + Add New Source
                    </button>
                </div>

                {/* Toolbar: Search, Filters & Create Button */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '30px'
                }}>
                    {/* Type Filters */}
                    <div style={{ display: 'flex', gap: '8px', background: '#e5eff8', padding: '6px', borderRadius: '12px' }}>
                        {[
                            { id: 'all', label: 'All Sources', icon: '📁' },
                            { id: 'image', label: 'Images', icon: '🖼️' },
                            { id: 'youtube_video', label: 'YouTube Videos', icon: '🎥' }
                        ].map((filter) => {
                            const isSelected = selectedType === filter.id;
                            return (
                                <button
                                    key={filter.id}
                                    onClick={() => handleTypeFilterChange(filter.id)}
                                    style={{
                                        border: 'none',
                                        background: isSelected ? '#3c5a5d' : 'transparent',
                                        color: isSelected ? '#ffffff' : '#475569',
                                        padding: '8px 16px',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <span>{filter.icon}</span>
                                    <span>{filter.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Search & Actions */}
                    <div style={{ display: 'flex', gap: '12px', flexGrow: 1, maxWidth: '500px' }}>
                        <input
                            type="text"
                            placeholder="Search by title, description, author, or URL..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                flexGrow: 1,
                                padding: '10px 16px',
                                borderRadius: '10px',
                                border: '1px solid #cbd5e1',
                                outline: 'none',
                                fontSize: '0.95rem',
                                background: '#ffffff',
                                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
                            }}
                        />
                        <button
                            onClick={() => navigate('/source-create')}
                            style={{
                                background: '#3c5a5d',
                                color: '#ffffff',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '10px',
                                fontWeight: '700',
                                fontSize: '0.95rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                whiteSpace: 'nowrap',
                                boxShadow: '0 4px 12px rgba(60, 90, 93, 0.25)'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#2c4345'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#3c5a5d'}
                        >
                            <span>+</span> Create Source
                        </button>
                    </div>
                </div>

                {/* Error Banner */}
                {error && (
                    <div style={{
                        color: '#dc2626',
                        marginBottom: '24px',
                        padding: '14px 18px',
                        background: '#fee2e2',
                        borderRadius: '12px',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span>{error}</span>
                        <button
                            onClick={() => fetchSources()}
                            style={{
                                background: '#dc2626',
                                color: '#ffffff',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600'
                            }}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Main Content Area */}
                {loading ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '80px 20px',
                        color: '#707F81'
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            border: '4px solid #cbd5e1',
                            borderTopColor: '#3c5a5d',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 16px auto'
                        }} />
                        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                        <p style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>Loading resources...</p>
                    </div>
                ) : filteredSources.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '80px 20px',
                        background: '#ffffff',
                        borderRadius: '16px',
                        border: '2px dashed #cbd5e1'
                    }}>
                        <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📂</div>
                        <h3 style={{ color: '#334155', margin: '0 0 8px 0', fontSize: '1.4rem' }}>No Sources Found</h3>
                        <p style={{ color: '#64748b', maxWidth: '400px', margin: '0 auto 24px auto', fontSize: '0.95rem' }}>
                            {searchQuery ? `No sources match "${searchQuery}".` : `You haven't added any ${selectedType === 'all' ? '' : selectedType} resources yet.`}
                        </p>
                        <button
                            onClick={() => navigate('/source-create')}
                            style={{
                                background: '#3c5a5d',
                                color: '#ffffff',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '10px',
                                fontWeight: '700',
                                cursor: 'pointer'
                            }}
                        >
                            + Create Source Now
                        </button>
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))',
                        gap: '24px'
                    }}>
                        {filteredSources.map((source, index) => {
                            const title = getSourceTitle(source);
                            const url = getSourceUrl(source);
                            const type = getSourceType(source);
                            const isYouTube = type === 'youtube_video';
                            const thumbUrl = isYouTube ? getYouTubeThumbnail(source) : (getSourceUrl(source) || getYouTubeThumbnail(source));
                            const description = getSourceDescription(source);
                            const authorOrTopic = getSourceAuthorOrTopic(source);

                            return (
                                <div
                                    key={source.id || source._id || index}
                                    style={{
                                        background: '#ffffff',
                                        borderRadius: '16px',
                                        overflow: 'hidden',
                                        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.05)',
                                        border: '1px solid #e2e8f0',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 4px 14px rgba(0, 0, 0, 0.05)';
                                    }}
                                >
                                    {/* Visual Header / Media Preview */}
                                    <div
                                        onClick={() => setActivePreview(source)}
                                        style={{
                                            position: 'relative',
                                            width: '100%',
                                            height: '190px',
                                            background: '#0f172a',
                                            cursor: 'pointer',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        {/* Type Badge Overlay */}
                                        <div style={{
                                            position: 'absolute',
                                            top: '12px',
                                            left: '12px',
                                            zIndex: 2,
                                            background: isYouTube ? '#dc2626' : '#2563eb',
                                            color: '#ffffff',
                                            padding: '4px 10px',
                                            borderRadius: '20px',
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                        }}>
                                            <span>{isYouTube ? '🎥' : '🖼️'}</span>
                                            <span>{isYouTube ? 'YouTube' : 'Image'}</span>
                                        </div>

                                        {/* Author / Topic Badge Overlay */}
                                        {authorOrTopic && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '12px',
                                                right: '12px',
                                                zIndex: 2,
                                                background: 'rgba(15, 23, 42, 0.75)',
                                                backdropFilter: 'blur(4px)',
                                                color: '#f8fafc',
                                                padding: '4px 10px',
                                                borderRadius: '20px',
                                                fontSize: '0.75rem',
                                                fontWeight: '600',
                                                maxWidth: '150px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                👤 {authorOrTopic}
                                            </div>
                                        )}

                                        {isYouTube ? (
                                            /* YouTube Video Thumbnail & Play Overlay */
                                            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                                                {thumbUrl ? (
                                                    <img
                                                        src={thumbUrl}
                                                        alt={title}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        background: '#1e293b',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#94a3b8'
                                                    }}>
                                                        Video Preview
                                                    </div>
                                                )}

                                                {/* Play Button Overlay */}
                                                <div style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    background: 'rgba(0, 0, 0, 0.35)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <div style={{
                                                        width: '54px',
                                                        height: '54px',
                                                        borderRadius: '50%',
                                                        background: '#dc2626',
                                                        color: '#ffffff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 4px 16px rgba(220, 38, 38, 0.5)',
                                                        fontSize: '1.4rem',
                                                        paddingLeft: '4px'
                                                    }}>
                                                        ▶
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            /* Image Preview Container */
                                            <img
                                                src={url}
                                                alt={title}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'https://via.placeholder.com/400x225?text=Image+Load+Error';
                                                }}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        )}
                                    </div>

                                    {/* Card Details */}
                                    <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                        <h3 style={{
                                            margin: '0 0 8px 0',
                                            color: '#1e293b',
                                            fontSize: '1.15rem',
                                            fontWeight: '700',
                                            lineHeight: '1.3',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical'
                                        }}>
                                            {title}
                                        </h3>

                                        {description && (
                                            <p style={{
                                                margin: '0 0 12px 0',
                                                color: '#64748b',
                                                fontSize: '0.88rem',
                                                lineHeight: '1.4',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical'
                                            }}>
                                                {description}
                                            </p>
                                        )}

                                        {/* URL Bar & Copy Action */}
                                        {url && (
                                            <div style={{
                                                margin: 'auto 0 16px 0',
                                                background: '#f1f5f9',
                                                borderRadius: '8px',
                                                padding: '8px 10px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: '8px'
                                            }}>
                                                <span style={{
                                                    color: '#475569',
                                                    fontSize: '0.8rem',
                                                    fontFamily: 'monospace',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {url}
                                                </span>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        copyToClipboard(url);
                                                    }}
                                                    title="Copy URL"
                                                    style={{
                                                        background: 'transparent',
                                                        border: 'none',
                                                        color: '#3c5a5d',
                                                        fontWeight: '700',
                                                        fontSize: '0.8rem',
                                                        cursor: 'pointer',
                                                        padding: '2px 6px',
                                                        borderRadius: '4px'
                                                    }}
                                                >
                                                    Copy
                                                </button>
                                            </div>
                                        )}

                                        {/* Actions Footer */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            paddingTop: '12px',
                                            borderTop: '1px solid #f1f5f9'
                                        }}>
                                            <button
                                                onClick={() => setActivePreview(source)}
                                                style={{
                                                    background: 'transparent',
                                                    border: '1px solid #cbd5e1',
                                                    color: '#334155',
                                                    padding: '6px 14px',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '600',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                👁️ View
                                            </button>

                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    onClick={() => navigate(`/source-edit/${source.id || source._id}`, { state: { source } })}
                                                    style={{
                                                        background: '#e0f2fe',
                                                        border: 'none',
                                                        color: '#0369a1',
                                                        padding: '6px 12px',
                                                        borderRadius: '8px',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '600',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    ✏️ Edit
                                                </button>

                                                <button
                                                    onClick={() => setDeletingSource(source)}
                                                    style={{
                                                        background: '#fee2e2',
                                                        border: 'none',
                                                        color: '#b91c1c',
                                                        padding: '6px 12px',
                                                        borderRadius: '8px',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '600',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    🗑️ Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* LIGHTBOX / VISUALIZATION MODAL */}
            {activePreview && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9999,
                    background: 'rgba(15, 23, 42, 0.85)',
                    backdropFilter: 'blur(6px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }} onClick={() => setActivePreview(null)}>
                    <div style={{
                        background: '#1e293b',
                        borderRadius: '20px',
                        maxWidth: '900px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                        border: '1px solid #334155'
                    }} onClick={(e) => e.stopPropagation()}>
                        {/* Modal Top Bar */}
                        <div style={{
                            padding: '16px 24px',
                            background: '#0f172a',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid #334155'
                        }}>
                            <div>
                                <h3 style={{ color: '#f8fafc', margin: '0 0 4px 0', fontSize: '1.2rem', fontWeight: '700' }}>
                                    {getSourceTitle(activePreview)}
                                </h3>
                                <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                                    Type: {getSourceType(activePreview) === 'youtube_video' ? 'YouTube Video' : 'Image'}
                                    {getSourceAuthorOrTopic(activePreview) ? ` • ${getSourceAuthorOrTopic(activePreview)}` : ''}
                                </span>
                            </div>
                            <button
                                onClick={() => setActivePreview(null)}
                                style={{
                                    background: '#334155',
                                    color: '#ffffff',
                                    border: 'none',
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    fontSize: '1.2rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Modal Media Body */}
                        <div style={{
                            padding: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#020617',
                            minHeight: '360px',
                            maxHeight: '60vh',
                            overflow: 'auto'
                        }}>
                            {getSourceType(activePreview) === 'youtube_video' ? (
                                <div style={{
                                    position: 'relative',
                                    width: '100%',
                                    paddingTop: '56.25%', // 16:9 Aspect Ratio
                                    borderRadius: '12px',
                                    overflow: 'hidden'
                                }}>
                                    <iframe
                                        src={getYouTubeEmbedUrl(activePreview)}
                                        title={getSourceTitle(activePreview)}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            border: 'none'
                                        }}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </div>
                            ) : (
                                <img
                                    src={getSourceUrl(activePreview)}
                                    alt={getSourceTitle(activePreview)}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '55vh',
                                        objectFit: 'contain',
                                        borderRadius: '8px'
                                    }}
                                />
                            )}
                        </div>

                        {/* Modal Footer Controls */}
                        <div style={{
                            padding: '16px 24px',
                            background: '#0f172a',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderTop: '1px solid #334155'
                        }}>
                            <span style={{ color: '#94a3b8', fontSize: '0.85rem', fontFamily: 'monospace', maxWidth: '500px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {getSourceUrl(activePreview)}
                            </span>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                {getSourceUrl(activePreview) && (
                                    <>
                                        <button
                                            onClick={() => copyToClipboard(getSourceUrl(activePreview))}
                                            style={{
                                                background: '#3c5a5d',
                                                color: '#ffffff',
                                                border: 'none',
                                                padding: '8px 16px',
                                                borderRadius: '8px',
                                                fontWeight: '600',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Copy URL
                                        </button>
                                        <button
                                            onClick={() => window.open(getSourceUrl(activePreview), '_blank')}
                                            style={{
                                                background: '#334155',
                                                color: '#ffffff',
                                                border: 'none',
                                                padding: '8px 16px',
                                                borderRadius: '8px',
                                                fontWeight: '600',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Open Link ↗
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* DELETE CONFIRMATION MODAL */}
            {deletingSource && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9999,
                    background: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '16px',
                        padding: '28px',
                        maxWidth: '450px',
                        width: '100%',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                    }}>
                        <h3 style={{ margin: '0 0 12px 0', color: '#1e293b', fontSize: '1.25rem' }}>
                            Delete Source?
                        </h3>
                        <p style={{ margin: '0 0 24px 0', color: '#64748b', fontSize: '0.95rem' }}>
                            Are you sure you want to delete <strong>"{getSourceTitle(deletingSource)}"</strong>? This action cannot be undone.
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button
                                onClick={() => setDeletingSource(null)}
                                disabled={isDeleting}
                                style={{
                                    background: '#f1f5f9',
                                    color: '#475569',
                                    border: 'none',
                                    padding: '10px 18px',
                                    borderRadius: '8px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={isDeleting}
                                style={{
                                    background: '#dc2626',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '10px 18px',
                                    borderRadius: '8px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SouceList;